import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import React from 'react'
import { useIntl } from 'react-intl'
import { draftBannerState, reviewAction } from '../../shared/draft-state'
import type { BoVersion } from '../../shared/types'

export interface DraftStateHeaderProps {
  changes?: number
  versions?: BoVersion[] | null
  /** Staging the draft. Absent while there is nothing to stage. */
  onPassToReview?: () => void
  passing?: boolean
  /**
   * The version already waiting for review, and whether the draft still matches it. `matchesDraft`
   * undefined means the comparison has not arrived, which reads as "there may be something new".
   */
  staged?: { version: number; matchesDraft?: boolean }
  /**
   * Open the change list. Absent when the caller has no diff to show, in which case the count
   * stays plain text - a control that opens nothing is worse than no control.
   */
  onShowChanges?: () => void
}

/**
 * Publish state for the collection on screen: what players have, how far the draft has drifted,
 * and the one action that moves it forward.
 *
 * A TOOLBAR GROUP, not a card. It used to be a `Paper`, which made it the only bordered box on
 * the row and 4px taller than the select beside it - the mismatch the owner spotted. A subtle
 * surface says "status, not action" without a second border competing with the page's panels, and
 * `minHeight` rather than `height` keeps it from clipping when a translation runs long or a user
 * scales their text up.
 *
 * NO LINK TO THE PUBLISH SCREEN. The sidebar already carries "Review & publish", and a second
 * copy of the same destination competed with `Pass to review` - the only action that acts on THIS
 * draft.
 */
export default function DraftStateHeader({
  changes = 0,
  versions,
  onPassToReview,
  passing = false,
  staged,
  onShowChanges,
}: DraftStateHeaderProps) {
  const { formatMessage } = useIntl()
  const state = draftBannerState(versions, changes)
  const action = reviewAction(
    state.changes,
    staged && staged.matchesDraft !== undefined
      ? { version: staged.version, matchesDraft: staged.matchesDraft }
      : undefined,
  )

  const headline = () => {
    switch (state.kind) {
      case 'live':
        return formatMessage(
          { id: 'avatarStudio.draft.live' },
          { version: state.version },
        )
      case 'needsApproval':
        return formatMessage(
          { id: 'avatarStudio.draft.needsApproval' },
          { version: state.version },
        )
      case 'approved':
        return formatMessage(
          { id: 'avatarStudio.draft.approved' },
          { version: state.version },
        )
      case 'nothingStaged':
        return formatMessage({ id: 'avatarStudio.draft.nothingStaged' })
      default:
        return formatMessage({ id: 'avatarStudio.draft.notPublished' })
    }
  }

  const label = () => {
    if (passing) {
      return formatMessage({ id: 'avatarStudio.draft.passing' })
    }

    return formatMessage({
      id:
        action.kind === 'passed'
          ? 'avatarStudio.draft.passedToReview'
          : 'avatarStudio.draft.passToReview',
    })
  }

  const hint = () => {
    if (action.kind === 'passed') {
      return formatMessage(
        { id: 'avatarStudio.draft.passedHint' },
        { version: action.version },
      )
    }
    if (action.kind === 'nothingToPass') {
      return formatMessage({ id: 'avatarStudio.draft.nothingToPass' })
    }

    return formatMessage({ id: 'avatarStudio.draft.passHint' })
  }

  const countLabel = formatMessage(
    { id: 'avatarStudio.draft.changesCount' },
    { count: state.changes },
  )
  const countSentence = formatMessage(
    { id: 'avatarStudio.draft.seeChangesAria' },
    { count: state.changes },
  )

  return (
    <Stack
      direction="row"
      alignItems="center"
      data-testid="draft-state"
      sx={{
        columnGap: 1,
        rowGap: 0.5,
        flexWrap: 'wrap',
        // Matches the select beside it. minHeight, never height - see the class comment.
        minHeight: 40,
        px: 1.25,
        py: 0.5,
        borderRadius: 1.5,
        bgcolor: 'action.hover',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <InfoOutlinedIcon fontSize="small" color="info" />
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {headline()}
      </Typography>

      {state.kind === 'live' && (
        <Chip
          size="small"
          color="success"
          label={formatMessage({ id: 'avatarStudio.publish.live' })}
        />
      )}

      {/* The count was the whole answer: an operator was told HOW MANY changes and had to leave
          for the publish screen to learn WHAT they were. The diff behind this number already
          holds the list, so the count opens it. A Chip WITH AN onClick is what earns the pointer,
          the hover state, the focus ring and `role="button"` - MUI infers all of it from the
          handler, so there is no `clickable` prop here (passing one changed nothing, measured).
          Plain text read as decoration, which is the bug this closes. */}
      {state.changes > 0 &&
        (onShowChanges ? (
          <Tooltip
            title={
              state.kind === 'live'
                ? formatMessage(
                    { id: 'avatarStudio.draft.seeChangesAgainstLive' },
                    { version: state.version },
                  )
                : formatMessage({ id: 'avatarStudio.draft.seeChanges' })
            }
          >
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              icon={<DifferenceOutlinedIcon />}
              label={countLabel}
              onClick={onShowChanges}
              data-testid="draft-state-changes"
              aria-label={countSentence}
              // A compact label belongs on one line. The full sentence is already the chip's
              // accessible name, so nothing depends on the hover-only tooltip to be understood.
              sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
            />
          </Tooltip>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="draft-state-changes"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {countLabel}
          </Typography>
        ))}

      {/*
       * The count changes as the operator edits, and a chip that silently renumbers itself is a
       * change a screen reader never hears. One atomic status carrying the SENTENCE, not the bare
       * number - and separate from the chip, which is a button and cannot also be a live region.
       */}
      <Box
        component="span"
        role="status"
        aria-atomic={true}
        style={visuallyHidden}
      >
        {state.changes > 0 ? countSentence : ''}
      </Box>

      {state.kind !== 'live' && (
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'avatarStudio.notice.nothingReachesPlayers' })}
        </Typography>
      )}

      {onPassToReview && (
        <Tooltip title={hint()}>
          {/* A disabled Button fires no pointer events, so the tooltip listens on a wrapper. */}
          <Box component="span" sx={{ ml: 'auto' }}>
            <Button
              size="small"
              variant="contained"
              disabled={passing || action.kind !== 'pass'}
              onClick={onPassToReview}
              data-testid="pass-to-review"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {label()}
            </Button>
          </Box>
        </Tooltip>
      )}
    </Stack>
  )
}
